import { useExtensions } from '../../lib/api/hooks.js';
import { useRingGroups, useQueues, useIvrs } from '../../lib/api/hooks-tms.js';
import type { Destination } from '@arcnet/contracts';

/**
 * Where a call goes.
 *
 * One control for every place a call can be sent, used by IVR options, inbound
 * routes and timeout destinations alike. The alternative — a bespoke selector
 * per screen — is how three screens end up offering three different subsets of
 * the same choices.
 */
export function DestinationPicker({ tenantId, value, onChange, allowNone = false }: {
  tenantId: string;
  value: Destination | null;
  onChange: (d: Destination | null) => void;
  allowNone?: boolean;
}) {
  const extensions = useExtensions(tenantId, { limit: 200 });
  const groups = useRingGroups(tenantId);
  const queues = useQueues(tenantId);
  const ivrs = useIvrs(tenantId);

  // Encoded as "type:id" so one <select> can express the whole union.
  const encoded = value === null ? ''
    : value.type === 'external' ? `external:${value.number}`
    : value.type === 'hangup' ? 'hangup:'
    : `${value.type}:${value.id}`;

  const decode = (raw: string): Destination | null => {
    if (!raw) return null;
    const [type, rest = ''] = raw.split(':');
    if (type === 'hangup') return { type: 'hangup' };
    if (type === 'external') return { type: 'external', number: rest };
    if (type === 'extension' || type === 'ring_group' || type === 'queue' || type === 'ivr') {
      return { type, id: rest };
    }
    return null;
  };

  return (
    <div className="arc-row" style={{ gap: 6 }}>
      <select
        className="arc-select"
        value={encoded.startsWith('external:') ? 'external:' : encoded}
        onChange={(e) => onChange(decode(e.target.value))}
        aria-label="Destination"
      >
        {allowNone && <option value="">— none —</option>}
        <optgroup label="Extensions">
          {extensions.data?.data.map((e) => (
            <option key={e.id} value={`extension:${e.id}`}>
              {e.number} · {e.display_name}
            </option>
          ))}
        </optgroup>
        <optgroup label="Hunt lists">
          {groups.data?.data.map((g) => (
            <option key={g.id} value={`ring_group:${g.id}`}>{g.name}</option>
          ))}
        </optgroup>
        <optgroup label="Queues">
          {queues.data?.data.map((q) => (
            <option key={q.id} value={`queue:${q.id}`}>{q.name}</option>
          ))}
        </optgroup>
        <optgroup label="IVRs">
          {ivrs.data?.data.map((m) => (
            <option key={m.id} value={`ivr:${m.id}`}>{m.name}</option>
          ))}
        </optgroup>
        <optgroup label="Other">
          <option value="external:">External number</option>
          <option value="hangup:">Hang up</option>
        </optgroup>
      </select>

      {value?.type === 'external' && (
        <input
          className="arc-input" style={{ maxWidth: 170 }}
          placeholder="+27821234567" value={value.number}
          onChange={(e) => onChange({ type: 'external', number: e.target.value })}
          aria-label="External number"
        />
      )}
    </div>
  );
}

/** Human-readable summary of a destination, for tables. */
export function DestinationLabel({ tenantId, destination }: {
  tenantId: string; destination: Destination | null;
}) {
  const extensions = useExtensions(tenantId, { limit: 200 });
  const groups = useRingGroups(tenantId);
  const queues = useQueues(tenantId);
  const ivrs = useIvrs(tenantId);

  if (!destination) return <span className="arc-subtle">not set</span>;

  switch (destination.type) {
    case 'external':
      return <span className="arc-mono">{destination.number}</span>;
    case 'hangup':
      return <span className="arc-subtle">hang up</span>;
    case 'extension': {
      const e = extensions.data?.data.find((x) => x.id === destination.id);
      return <span>{e ? `${e.number} · ${e.display_name}` : 'extension'}</span>;
    }
    case 'ring_group': {
      const g = groups.data?.data.find((x) => x.id === destination.id);
      return <span>{g?.name ?? 'hunt list'}</span>;
    }
    case 'queue': {
      const q = queues.data?.data.find((x) => x.id === destination.id);
      return <span>{q?.name ?? 'queue'}</span>;
    }
    case 'ivr': {
      const m = ivrs.data?.data.find((x) => x.id === destination.id);
      return <span>{m?.name ?? 'IVR'}</span>;
    }
    default:
      return <span className="arc-subtle">—</span>;
  }
}
